import { IconProps } from '@amzn/awsui-components-console/icon';
import iconTeamsNoCms from 'src/images/icon-teams-no-cms.svg';

/** Every user-facing field stores a resource id, and the fields are named `*Id` deliberately: a
 * call site still rendering a raw literal fails to compile rather than shipping untranslated. */

/** URLs */
export const PILOT_URL =
  'https://forms.cloud.microsoft/pages/responsepage.aspx?id=ShCAUi1HOEWczx4dDv6LGy-0rtE_qJtNhDYu3fd0hVZUMDlaNTI1S0ZQQkJERDdaNlhIMlBJR0VQNSQlQCN0PWcu&route=shorturl';
export const SURVEY_URL =
  'https://forms.cloud.microsoft/pages/responsepage.aspx?id=ShCAUi1HOEWczx4dDv6LGyzXzV01jN5GkW6ceQj7iqJUMUE3WDNNNjJXUFJaTjdDNEJPMkRJWFhNTy4u&route=shorturl';

export interface FeatureCard {
  iconName: IconProps.Name;
  titleId: string;
  descriptionId: string;
  badgeId?: string;
}

export interface UserTypeCard {
  iconName?: IconProps.Name;
  iconSvg?: string;
  titleId: string;
  descriptionId: string;
}

const COMING_SOON_BADGE_ID = 'landing-feature-badge-coming-soon';

export const features: FeatureCard[] = [
  {
    iconName: 'edit',
    titleId: 'landing-feature-create-title',
    descriptionId: 'landing-feature-create-desc',
  },
  {
    iconName: 'globe',
    titleId: 'landing-feature-hands-free-title',
    descriptionId: 'landing-feature-hands-free-desc',
  },
  {
    iconName: 'key',
    titleId: 'landing-feature-access-title',
    descriptionId: 'landing-feature-access-desc',
  },
  {
    iconName: 'refresh',
    titleId: 'landing-feature-track-title',
    descriptionId: 'landing-feature-track-desc',
  },
  {
    iconName: 'folder',
    titleId: 'landing-feature-variants-title',
    descriptionId: 'landing-feature-variants-desc',
  },
  {
    iconName: 'gen-ai',
    titleId: 'landing-feature-llm-title',
    descriptionId: 'landing-feature-llm-desc',
  },
  {
    iconName: 'star-filled',
    titleId: 'landing-feature-quality-title',
    descriptionId: 'landing-feature-quality-desc',
  },
  {
    iconName: 'check',
    titleId: 'landing-feature-review-title',
    descriptionId: 'landing-feature-review-desc',
    badgeId: COMING_SOON_BADGE_ID,
  },
  {
    iconName: 'file',
    titleId: 'landing-feature-edit-translation-title',
    descriptionId: 'landing-feature-edit-translation-desc',
    badgeId: COMING_SOON_BADGE_ID,
  },
  {
    iconName: 'upload-download',
    titleId: 'landing-feature-bulk-title',
    descriptionId: 'landing-feature-bulk-desc',
    badgeId: COMING_SOON_BADGE_ID,
  },
  {
    iconName: 'settings',
    titleId: 'landing-feature-testing-title',
    descriptionId: 'landing-feature-testing-desc',
    badgeId: COMING_SOON_BADGE_ID,
  },
];

export const userTypes: UserTypeCard[] = [
  {
    iconSvg: iconTeamsNoCms,
    titleId: 'landing-user-type-no-cms-title',
    descriptionId: 'landing-user-type-no-cms-desc',
  },
  {
    iconName: 'upload-download',
    titleId: 'landing-user-type-ast-title',
    descriptionId: 'landing-user-type-ast-desc',
  },
  {
    iconName: 'globe',
    titleId: 'landing-user-type-panther-title',
    descriptionId: 'landing-user-type-panther-desc',
  },
  {
    iconName: 'history',
    titleId: 'landing-user-type-lui-title',
    descriptionId: 'landing-user-type-lui-desc',
  },
];

/** Roadmap */
export interface RoadmapItem {
  milestoneId: string;
  capabilitiesId: string;
  targetId: string;
}

export const ROADMAP_ITEMS: RoadmapItem[] = [
  {
    milestoneId: 'landing-roadmap-mvp-milestone',
    capabilitiesId: 'landing-roadmap-mvp-capabilities',
    targetId: 'landing-roadmap-mvp-target',
  },
  {
    milestoneId: 'landing-roadmap-m2-milestone',
    capabilitiesId: 'landing-roadmap-m2-capabilities',
    targetId: 'landing-roadmap-m2-target',
  },
  {
    milestoneId: 'landing-roadmap-m3-milestone',
    capabilitiesId: 'landing-roadmap-m3-capabilities',
    targetId: 'landing-roadmap-m3-target',
  },
  {
    milestoneId: 'landing-roadmap-m4-milestone',
    capabilitiesId: 'landing-roadmap-m4-capabilities',
    targetId: 'landing-roadmap-m4-target',
  },
  {
    milestoneId: 'landing-roadmap-migration-milestone',
    capabilitiesId: 'landing-roadmap-migration-capabilities',
    targetId: 'landing-roadmap-migration-target',
  },
];

/** AST vs LangHub comparison */
export interface ComparisonItem {
  capabilityId: string;
  astId: string;
  langhubId: string;
  comingSoon: boolean;
}

export const COMPARISON_ITEMS: ComparisonItem[] = [
  {
    capabilityId: 'landing-comparison-llm-model-capability',
    astId: 'landing-comparison-llm-model-ast',
    langhubId: 'landing-comparison-llm-model-langhub',
    comingSoon: false,
  },
  {
    capabilityId: 'landing-comparison-coverage-capability',
    astId: 'landing-comparison-coverage-ast',
    langhubId: 'landing-comparison-coverage-langhub',
    comingSoon: false,
  },
  {
    capabilityId: 'landing-comparison-variants-capability',
    astId: 'landing-comparison-variants-ast',
    langhubId: 'landing-comparison-variants-langhub',
    comingSoon: false,
  },
  {
    capabilityId: 'landing-comparison-source-review-capability',
    astId: 'landing-comparison-source-review-ast',
    langhubId: 'landing-comparison-source-review-langhub',
    comingSoon: true,
  },
  {
    capabilityId: 'landing-comparison-overwrite-capability',
    astId: 'landing-comparison-overwrite-ast',
    langhubId: 'landing-comparison-overwrite-langhub',
    comingSoon: true,
  },
  {
    capabilityId: 'landing-comparison-bulk-capability',
    astId: 'landing-comparison-bulk-ast',
    langhubId: 'landing-comparison-bulk-langhub',
    comingSoon: true,
  },
  {
    capabilityId: 'landing-comparison-cancel-capability',
    astId: 'landing-comparison-cancel-ast',
    langhubId: 'landing-comparison-cancel-langhub',
    comingSoon: false,
  },
  {
    capabilityId: 'landing-comparison-search-capability',
    astId: 'landing-comparison-search-ast',
    langhubId: 'landing-comparison-search-langhub',
    comingSoon: false,
  },
  {
    capabilityId: 'landing-comparison-tracking-capability',
    astId: 'landing-comparison-tracking-ast',
    langhubId: 'landing-comparison-tracking-langhub',
    comingSoon: false,
  },
  {
    capabilityId: 'landing-comparison-notifications-capability',
    astId: 'landing-comparison-notifications-ast',
    langhubId: 'landing-comparison-notifications-langhub',
    comingSoon: false,
  },
  {
    capabilityId: 'landing-comparison-access-capability',
    astId: 'landing-comparison-access-ast',
    langhubId: 'landing-comparison-access-langhub',
    comingSoon: false,
  },
  {
    capabilityId: 'landing-comparison-unit-capability',
    astId: 'landing-comparison-unit-ast',
    langhubId: 'landing-comparison-unit-langhub',
    comingSoon: false,
  },
  {
    capabilityId: 'landing-comparison-staging-capability',
    astId: 'landing-comparison-staging-ast',
    langhubId: 'landing-comparison-staging-langhub',
    comingSoon: true,
  },
  {
    capabilityId: 'landing-comparison-chatbot-capability',
    astId: 'landing-comparison-chatbot-ast',
    langhubId: 'landing-comparison-chatbot-langhub',
    comingSoon: true,
  },
];
